import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getMockConfig } from 'domains/appContext/AppContext.store';
import { getIsSessionActive } from 'domains/delegation';
import { getUserRights, UserRole } from 'domains/user';
import DmsSettings from 'pages/SettingsPage/Dms';
import { EstimateSettings } from 'pages/SettingsPage/Estimates/EstimateSettings';
import GeneralSettings from 'pages/SettingsPage/General';
import { MockSettings } from 'pages/SettingsPage/Mock/MockSettings';
import { NotificationSettings } from 'pages/SettingsPage/NotificationCenter/NotificationSettings';
import { SellerSettings } from 'pages/SettingsPage/Seller/SellerSettings';
import { DMS, ESTIMATES, GENERAL, MOCK, NOTIFICATIONS, SELLER, TIRES } from 'pages/SettingsPage/SettingsPage.types';
import { TiresPreferencesSettings } from 'pages/SettingsPage/TiresPreferences/TiresPreferencesSettings';
import { Flex, LinkTabs, Text } from 'UI';
import { getData, hasUserAnyRight } from 'utils';
import { Container } from './ProfilePage.styled';

const SettingsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const userRights = getData(useSelector(getUserRights));
  const isDelegationSessionActive = useSelector(getIsSessionActive);
  const mockConfig = useSelector(getMockConfig);

  const navigationItems = useMemo(
    () =>
      [
        {
          key: 0,
          label: t('settings.general', 'General'),
          link: `/${GENERAL}`,
          component: <GeneralSettings />,
          requiredRights: [],
          dataCy: 'settings-general-button',
        },
        {
          key: 1,
          label: t('settings.notification_center', 'Notification center'),
          link: `/${NOTIFICATIONS}`,
          component: <NotificationSettings />,
          requiredRights: [],
          dataCy: 'settings-notifications-button',
        },
        {
          key: 2,
          label: t('settings.estimates', 'Estimates'),
          link: `/${ESTIMATES}`,
          component: <EstimateSettings />,
          requiredRights: [UserRole.COMMAND, UserRole.CONNECT_COMMANDE],
          dataCy: 'settings-estimates-button',
        },
        {
          key: 3,
          label: t('settings.tires_preferences', 'Tires preferences'),
          link: `/${TIRES}`,
          component: <TiresPreferencesSettings />,
          requiredRights: [UserRole.COMMAND],
          dataCy: 'settings-tires-preferences-button',
        },
        {
          key: 4,
          label: t('settings.dms', 'DMS'),
          link: `/${DMS}`,
          component: <DmsSettings />,
          requiredRights: [UserRole.R1, UserRole.R2, UserRole.R3, UserRole.R3_MOTRIO],
          dataCy: 'settings-dms-button',
        },
        {
          key: 5,
          label: t('settings.seller', 'Seller'),
          link: `/${SELLER}`,
          component: <SellerSettings />,
          requiredRights: [UserRole.COMMAND],
          hidden: isDelegationSessionActive,
          dataCy: 'settings-seller-button',
        },
        {
          key: 6,
          label: t('settings.mock', 'Mock'),
          link: `/${MOCK}`,
          component: <MockSettings />,
          requiredRights: [],
          hidden: !mockConfig || Object.keys(mockConfig)?.length === 0,
          dataCy: 'settings-mock-button',
        },
      ].filter((tab) => hasUserAnyRight(tab.requiredRights, userRights) && !tab.hidden),
    [t, userRights, isDelegationSessionActive, mockConfig],
  );

  return (
    <Flex direction={'column'} align={'center'}>
      <Container data-cy={'settings-page'}>
        <Text type={'h1_black'} data-cy={'settings-title'}>
          {t('settings.title', 'Settings')}
        </Text>
        <LinkTabs variant={'greyBlack'} items={navigationItems} />
      </Container>
    </Flex>
  );
};

export default SettingsPage;
